<template>
  <div>
    <CommonSection />
    <div class="container-fluid">
      <v-row>
        <v-col>
          <card shadow type="secondary">
            <div slot="header" class="bg-white border-0">
              <h3 class="mb-0 float-left card-header-name">
                News/Articles List
              </h3>
              <router-link
                :to="{ path: 'news-feed/create' }"
                class="float-right mt-0"
              >
                <v-tooltip
                  bottom
                  :color="$vuetify.theme.themes.light.tooltipColor"
                >
                  <template v-slot:activator="{ on: tooltip, attrs }">
                    <img
                      class="mr-2"
                      v-bind="attrs"
                      v-on="{ ...tooltip }"
                      src="@/assets/images/Plus.svg"
                    />
                  </template>
                  <span>Add News & Articles</span>
                </v-tooltip>
              </router-link>
            </div>
            <v-card>
              <v-card-title>
                <v-row>
                  <v-col>
                    <v-text-field
                      class="search-text-field"
                      v-model="search"
                      append-icon="search"
                      label="Search by Title"
                      single-line
                      hide-details
                      v-debounce:1s="searchFilter"
                      :debounce-events="['click', 'keydown']"
                    ></v-text-field>
                  </v-col>

                  <v-col>
                    <v-select
                      :items="categoryArr"
                      item-value="_id"
                      item-text="name"
                      label="Select Category"
                      v-model="category"
                      clearable
                      @change="statusFilter"
                    ></v-select>
                  </v-col>

                  <v-col>
                    <v-select
                      :items="statusArr"
                      item-value="value"
                      item-text="name"
                      label="Select Status"
                      v-model="status"
                      @change="statusFilter"
                    ></v-select>
                  </v-col>

                  <v-col>
                    <v-select
                      :items="recommendedArr"
                      item-value="value"
                      item-text="name"
                      label="Select Recommendation"
                      v-model="recommendation"
                      @change="recommendationFilter"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-data-table
                :headers="headers"
                :items="newsFeed"
                :loading="loading"
                loading-text="Loading... Please wait"
                :server-items-length="newsFeed.length"
                @update:page="initialize()"
                @update:items-per-page="initialize()"
                @update:sort-desc="initialize()"
                :options.sync="options"
                hide-default-footer
              >
                <template v-slot:[`item.title`]="{ item }">
                  <div>{{ item.title }}</div>
                </template>

                <template v-slot:[`item.publishedDate`]="{ item }">
                  <div v-if="item.publishedDate">{{ item.publishedDate | moment("LL") }}</div>
                  <div v-else>--</div>
                </template>

                <template v-slot:[`item.categoryObj.name`]="{ item }">
                  <div>{{ item.categoryObj.name }}</div>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                  <div>{{ item.status === 1 ? 'Published' : 'Draft' }}</div>
                </template>

                <template v-slot:[`item.isRecommended`]="{ item }">
                  <v-switch class="pl-4 pt-3 mt-0" v-model="item.isRecommended" @change="updateRecommendation(item)" flat:label="Yes" inset></v-switch>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <router-link :to="{ path: 'news-feed/' + item._id }">
                    <v-tooltip
                      bottom
                      :color="$vuetify.theme.themes.light.tooltipColor"
                    >
                      <template v-slot:activator="{ on: tooltip, attrs }">
                        <img
                          v-bind="attrs"
                          v-on="{ ...tooltip }"
                          class="mr-2 edit-icon"
                          src="@/assets/images/Edit.svg"
                        />
                      </template>
                      <span>Edit</span>
                    </v-tooltip>
                  </router-link>

                  <delete-component
                    :id="item._id"
                    module="newsFeed/delete"
                    @requestDeleted="requestDeleted"
                  ></delete-component>
                </template>
              </v-data-table>

              <v-card-actions class="justify-end pt-2">
                <v-pagination
                  circle
                  v-model="options.page"
                  :length="options.pageCount"
                ></v-pagination>
              </v-card-actions>
            </v-card>
          </card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import CommonSection from "@/components/CommonHeader";
import DeleteComponent from "@/components/DeleteComponent";
import { NewsfeedService } from "@/services";

export default {
  name: "NewsFeed",
  components: {
    CommonSection,
    DeleteComponent,
  },
  data: () => ({
    options: {
      page: 1,
      itemsPerPage: 50,
      pageCount: 0,
    },
    loading: true,
    newsFeed: [],
    search: "",
    status: 1,
    category: null,
    categoryArr: [],
    recommendation : 'all',
    statusArr: [
      {
        name: "Draft",
        value: 3,
      },
      {
        name: "Published",
        value: 1,
      },
    ],
    recommendedArr: [
       {
        name: "All",
        value: 'all',
      },
      {
        name: "Yes",
        value: true,
      },
      {
        name: "No",
        value: false,
      },
    ],
    headers: [
      {
        text: "Title",
        align: "start",
        value: "title",
      },
      { text: "Publish Date", value: "publishedDate",width:180},
      { text: "Category", value: "categoryObj.name", sortable: false  },
      { text: "Status", value: "status", sortable: false  },
      { text: "Recommended", value: "isRecommended", sortable: false  },
      { text: "Actions", value: "actions", sortable: false },
    ],
  }),
  watch: {
    page() {
      this.initialize();
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.loading = true;
      const params = {
        limit: parseInt(this.options.itemsPerPage),
        skip:
          parseInt(this.options.page) * parseInt(this.options.itemsPerPage) -
          parseInt(this.options.itemsPerPage),
        title: this.search,
        newsCategory: this.category,
        sortName: this.options.sortBy && this.options.sortBy[0],
        sortBy: this.options.sortDesc && this.options.sortDesc[0],
      };
      params.status = this.status ? this.status : 1;
      if (this.recommendation !== 'all') {
        params.isRecommended = this.recommendation;
      }
      
      const newsfeeds = await NewsfeedService.newsFeedList(params);
      const categoryList = newsfeeds.data.data.map((obj) => {
        return obj.categoryObj;
      });
      this.categoryArr = categoryList;
      if (this.category) {
        params.newsCategory = this.category;
      }
      if (newsfeeds.data.data.length > 0) {
        this.options.pageCount = Math.ceil(
          newsfeeds.data.option[0].count / this.options.itemsPerPage
        );
        this.newsFeed = newsfeeds.data.data;
        this.loading = false;
      } else {
        if (this.options.page !== 1) {
          this.options.page--;
        }
        this.loading = false;
        this.newsFeed = [];
      }
    },
    async handleClick(value) {
      this.$router.push("/news-feed/" + value._id);
    },
    async updateRecommendation(item) {
        const response = await NewsfeedService.updateNewsFeed({
            id: item._id,
            object: {
              isRecommended : item.isRecommended,
              status: item.status
            },
        });
        if (response) {
          this.$store.dispatch(
            "user/setSnack",
            "Newsfeed updated successfully!"
          );
          this.$store.dispatch("user/setType", "info");
        } else {
          item.isRecommended = false;
        }
    },
    searchFilter() {
      this.options.page = 1;
      this.initialize();
    },
    statusFilter() {
      this.options.page = 1;
      this.initialize();
    },
    requestDeleted() {
      this.initialize();
    },
    recommendationFilter() {
      this.options.page = 1;
      this.initialize();
    }
  },
};
</script>
